import { useState, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { AuthContext } from './AuthContext';
import type { AuthContextType, AuthUser } from '../types';

// Mock admin user for demonstration
const MOCK_ADMIN: AuthUser = {
  id: '1',
  email: 'admin@sternwarte-mannheim.de',
  name: 'Administrator',
  role: 'admin',
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Mock authentication - in production, this would call an actual API
    if (email === 'admin@sternwarte-mannheim.de' && password === 'admin123') {
      setUser(MOCK_ADMIN);
      // Store in localStorage for persistence
      localStorage.setItem('admin_auth', JSON.stringify(MOCK_ADMIN));
      setIsLoading(false);
      return true;
    }
    
    setIsLoading(false);
    return false;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('admin_auth');
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const stored = localStorage.getItem('admin_auth');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
      } catch {
        localStorage.removeItem('admin_auth');
      }
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Protected route wrapper hook
export function useRequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  return { isAuthenticated, isLoading };
}
